import { useCallback, useRef } from "react";

/*
 * useConcurrencyQueue custom hook
 * Runs enqueued async tasks with at most `maxConcurrent` running at once, queueing the rest
 * and starting each as a slot frees up. Queue state lives in refs so enqueuing doesn't trigger renders.
 */
export const useConcurrencyQueue = (maxConcurrent: number) => {
  const queueRef = useRef<Array<() => void>>([]);
  const activeCountRef = useRef(0);

  const processQueue = useCallback(() => {
    // Claim a slot before running each task, so repeated calls can't overshoot maxConcurrent.
    while (activeCountRef.current < maxConcurrent && queueRef.current.length > 0) {
      const runNext = queueRef.current.shift();
      if (!runNext) break;
      activeCountRef.current += 1;
      runNext();
    }
  }, [maxConcurrent]);

  const enqueue = useCallback(
    <T,>(task: () => Promise<T>): Promise<T> => {
      // resolve/reject are captured below so the caller gets this task's own result via the promise.
      return new Promise<T>((resolve, reject) => {
        // Queue a runner (not the task itself) that frees its slot and advances the queue when done.
        queueRef.current.push(() => {
          task()
            .then(resolve, reject)
            .finally(() => {
              activeCountRef.current -= 1;
              processQueue();
            });
        });

        // Runs immediately if a slot is free; otherwise this task waits its turn in the queue.
        processQueue();
      });
    },
    [processQueue],
  );

  return { enqueue };
};
